import { useContext, useState, useEffect, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { UserContext } from "../../context/UserContext";
import api, { NotificationsAPI } from "../../api";
import "./header.css";
import { resolveMediaUrl } from "../../utils/url";
import AvatarImg from "../AvatarImg";

export default function Header() {
  const { user, logout } = useContext(UserContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [unreadPrivateCount, setUnreadPrivateCount] = useState(0); // unread private chats
  const [systemUnreadCount, setSystemUnreadCount] = useState(0); // unread system messages
  const [notifCount, setNotifCount] = useState(0);   // in-app notifications
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const nav = useNavigate();

  // Unread count holen
  useEffect(() => {
    if (!user) return;
    async function fetchUnread() {
      try {
        const { data } = await api.get("/messages/conversations");
        // Zähle alle Konversationen mit ungelesener Nachricht
        const count = (data || []).filter(c => c.lastMessage && !c.lastRead && Number(c.lastSenderId) !== Number(user.id)).length;
        setUnreadPrivateCount(count);
      } catch {
        setUnreadPrivateCount(0);
      }
    }
    async function fetchSystemUnread() {
      try {
        // Holt alle System-Nachrichten des Users und zählt die ungelesenen
        const { data } = await api.get('/rundmail/intern');
        const items = Array.isArray(data) ? data : [];
        // Zähle alles, was nicht als gelesen markiert ist (falsey)
        const unread = items.filter(m => m && !m.read).length;
        setSystemUnreadCount(unread);
      } catch {
        setSystemUnreadCount(0);
      }
    }
    const run = async () => { await Promise.all([fetchUnread(), fetchSystemUnread()]); };
    run();
    // Optional: alle 15s poll für Live-Badge
    const timer = setInterval(run, 15000);
    return () => clearInterval(timer);
  }, [user]);

  // Notifications unread count + list polling
  useEffect(() => {
    if (!user) return;
    async function fetchNotifs() {
      try {
        const { count } = await NotificationsAPI.unreadCount();
        setNotifCount(count || 0);
        if (notifOpen) {
          const res = await NotificationsAPI.list({ onlyUnread: true, limit: 10 });
          setNotifs(res?.items || []);
        }
      } catch {
        setNotifCount(0);
      }
    }
    fetchNotifs();
    const t = setInterval(fetchNotifs, 20000);
    return () => clearInterval(t);
  }, [user, notifOpen]);

  async function toggleNotif() {
    const next = !notifOpen;
    setNotifOpen(next);
    if (next) {
      try {
        const res = await NotificationsAPI.list({ onlyUnread: true, limit: 10 });
        setNotifs(res?.items || []);
      } catch { setNotifs([]); }
    }
  }

  async function markRead(n) {
    try { await NotificationsAPI.markRead(n.id); } catch {}
    setNotifs(arr => arr.filter(x => x.id !== n.id));
    setNotifCount(c => Math.max(0, c - 1));
    if (n.data?.link) {
      nav(n.data.link);
      setNotifOpen(false);
    }
  }

  const handleLogout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    logout();
    nav("/login");
  };

  const initials = (user?.displayName || user?.username)
    ? (user.displayName || user.username).trim().split(/\s+/).slice(0,2).map(s=>s[0]?.toUpperCase()).join("")
    : "";
  // Cache-Busting: Zeitstempel an avatarUrl, wenn vorhanden; env-aware resolution
  const rawAvatar = user?.avatarUrl ? (user.avatarUrl + (user.avatarUrl.includes("?") ? '' : ('?t=' + Date.now()))) : '/avatars/default-avatar.png';
  const avatarUrl = resolveMediaUrl(rawAvatar);

  // Close any open dropdown by removing focus after selecting a submenu item
  function handleSubmenuItemClick() {
    if (menuOpen) setMenuOpen(false);
    // Defer blur to after navigation triggers
    requestAnimationFrame(() => {
      const ae = document.activeElement;
      if (ae && typeof ae.blur === 'function') {
        ae.blur();
      }
    });
  }

  return (
  <header className="apn-header" role="banner" style={{width:'100%',margin:0,padding:'0 32px'}}>
      {/* Skip-Link für Accessibility */}
      <a href="#main" className="apn-skip">Zum Inhalt springen</a>

  <div className="apn-header-row" style={{width:'100%',margin:0,padding:0, display:'flex', justifyContent:'space-between', alignItems:'center'}}>
        {/* Logo */}
  <Link to="/" className="apn-header-logo" aria-label="AP-Network Startseite" style={{marginLeft:0}}>
          <img src="/apn-logo.png" alt="" className="apn-logo-img" />
          <img src="/apn-wordmark.png" alt="AP-Network" className="apn-logo-text" />
        </Link>

        {/* Burger (mobil) */}
        <button
          className="apn-burger"
          aria-label="Menü öffnen/schließen"
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(v => !v)}
        >
          <span className="apn-burger-bar" />
          <span className="apn-burger-bar" />
          <span className="apn-burger-bar" />
        </button>

        {/* Navigation - gruppierte Dropdown-Struktur */}
        <nav className={menuOpen ? "apn-menu-mobile-open" : "apn-menu-desktop"} aria-label="Hauptnavigation">
          <ul className="apn-menu" onClick={() => setMenuOpen(false)}>
            {/* Startseite / Dashboard (bedingt) */}
            <li>
              {user ? (
                <NavLink to="/dashboard">Dashboard</NavLink>
              ) : (
                <NavLink to="/" end>Startseite</NavLink>
              )}
            </li>

            {/* Infos Gruppe */}
            <li className="nav-group" aria-haspopup="true">
              <span className="nav-group-trigger" tabIndex={0} aria-expanded="false">Infos ▾</span>
              <ul className="apn-submenu" role="menu" aria-label="Infos Untermenü">
                <li role="none"><NavLink role="menuitem" to="/ueber-ap-network" onClick={handleSubmenuItemClick}>Über das AP-Network</NavLink></li>
                <li role="none"><NavLink role="menuitem" to="/wie-funktioniert-ap-n" onClick={handleSubmenuItemClick}>Wie funktioniert das AP-N</NavLink></li>
                <li role="none"><NavLink role="menuitem" to="/team-ap-n" onClick={handleSubmenuItemClick}>Das Team des AP-N</NavLink></li>
                <li role="none"><NavLink role="menuitem" to="/mentoren-programm" onClick={handleSubmenuItemClick}>Mentoren-Programm</NavLink></li>
                <li role="none"><NavLink role="menuitem" to="/jobs" onClick={handleSubmenuItemClick}>Jobs im AP-Network</NavLink></li>
              </ul>
            </li>

            {/* Leistungen Gruppe */}
            <li className="nav-group" aria-haspopup="true">
              <span className="nav-group-trigger" tabIndex={0} aria-expanded="false">Leistungen ▾</span>
              <ul className="apn-submenu" role="menu" aria-label="Leistungen Untermenü">
                <li role="none"><NavLink role="menuitem" to="/angebote" onClick={handleSubmenuItemClick}>Überblick unserer Angebote</NavLink></li>
                <li role="none"><NavLink role="menuitem" to="/leistungen-im-detail" onClick={handleSubmenuItemClick}>Unsere Leistungen im Detail</NavLink></li>
                <li role="none"><NavLink role="menuitem" to="/firmen-und-vereine-b2b" onClick={handleSubmenuItemClick}>Speziell für Firmen & Vereine (B2B)</NavLink></li>
                <li role="none"><NavLink role="menuitem" to="/fuer-communitys" onClick={handleSubmenuItemClick}>Speziell für Communitys</NavLink></li>
              </ul>
            </li>

            {/* Community Gruppe */}
            <li className="nav-group" aria-haspopup="true">
              <span className="nav-group-trigger" tabIndex={0} aria-expanded="false">Community ▾</span>
              <ul className="apn-submenu" role="menu" aria-label="Community Untermenü">
                <li role="none"><NavLink role="menuitem" to="/mitglieder" onClick={handleSubmenuItemClick}>Unsere Mitglieder</NavLink></li>
                <li role="none"><NavLink role="menuitem" to="/shops" onClick={handleSubmenuItemClick}>Unsere Shops</NavLink></li>
                <li role="none"><NavLink role="menuitem" to="/servers" onClick={handleSubmenuItemClick}>Unsere Server</NavLink></li>
                <li role="none"><NavLink role="menuitem" to="/unsere-partner" onClick={handleSubmenuItemClick}>Unsere Partner</NavLink></li>
              </ul>
            </li>

            {/* Rechtliches Gruppe */}
            <li className="nav-group" aria-haspopup="true">
              <span className="nav-group-trigger" tabIndex={0} aria-expanded="false">Rechtliches ▾</span>
              <ul className="apn-submenu" role="menu" aria-label="Rechtliches Untermenü">
                <li role="none"><NavLink role="menuitem" to="/agb" onClick={handleSubmenuItemClick}>AGB</NavLink></li>
                <li role="none"><NavLink role="menuitem" to="/datenschutz" onClick={handleSubmenuItemClick}>Datenschutz</NavLink></li>
                <li role="none"><NavLink role="menuitem" to="/impressum" onClick={handleSubmenuItemClick}>Impressum</NavLink></li>
                <li role="none"><NavLink role="menuitem" to="/kontakt" onClick={handleSubmenuItemClick}>Kontakt</NavLink></li>
              </ul>
            </li>

            {/* Admin Direktlink bleibt außerhalb der Gruppen */}
            {user?.role === "admin" && (
              <li><NavLink to="/admin/users">Benutzerverwaltung</NavLink></li>
            )}
          </ul>
        </nav>

        {/* Rechts: Account-Menü mit Avatar und Name */}
  <div className="apn-header-actions" style={{display:'flex', alignItems:'center', gap:16, marginRight:0}}>
          {!user ? (
            <>
              <Link className="apn-header-btn" to="/login">Login</Link>
              <Link className="apn-signup-btn" to="/register">Sign-up</Link>
            </>
          ) : (
            <div className="apn-account">
              <button
                className="apn-account-trigger"
                aria-expanded={accountOpen}
                onClick={() => setAccountOpen(o => !o)}
                title={`${user.username} (${user.role})`}
                style={{display:'flex', alignItems:'center', gap:8, background:'none', border:'none', cursor:'pointer', padding:0}}
              >
                <AvatarImg src={avatarUrl} alt="Avatar" size={36} style={{ border:'2px solid #32CD89' }} />
                <span className="apn-account-name" style={{fontWeight:500, fontSize:15}}>
                  {user.displayName || user.username}
                </span>
              </button>
              {/* Notifications bell – rechts neben dem Profilnamen, kombiniert: Benachrichtigungen + ungelesene Chats */}
              <button onClick={toggleNotif} aria-label="Benachrichtigungen" title="Benachrichtigungen" style={{ position:'relative', background:'none', border:'none', color:'#fff', cursor:'pointer', fontSize:20, marginLeft:8, paddingLeft:6, paddingRight:6 }}>
                🔔
                {((notifCount || 0) + (unreadPrivateCount || 0) + (systemUnreadCount || 0)) > 0 && (
                  <span style={{ position:'absolute', top:-6, right:-10, background:'#ff4757', color:'#fff', borderRadius:12, fontSize:12, fontWeight:700, minWidth:20, height:20, display:'inline-flex', alignItems:'center', justifyContent:'center', padding:'0 6px', boxShadow:'0 2px 8px #0002', border:'2px solid #111' }}>{(notifCount || 0) + (unreadPrivateCount || 0) + (systemUnreadCount || 0)}</span>
                )}
              </button>
              {notifOpen && (
                <div className="apn-account-menu" onMouseLeave={() => setNotifOpen(false)} style={{ maxWidth:340 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div style={{ fontWeight:700 }}>Benachrichtigungen</div>
                    <button
                      onClick={async ()=>{
                        try {
                          await NotificationsAPI.markAllRead();
                          // Chat: alles als gelesen markieren (privat + system)
                          await api.post('/messages/mark-all-read');
                          setNotifs([]);
                          setNotifCount(0);
                          setUnreadPrivateCount(0);
                          setSystemUnreadCount(0);
                        } catch {}
                      }}
                      style={{ fontSize:12 }}
                    >
                      Alle gelesen
                    </button>
                  </div>
                  <div style={{ maxHeight:280, overflow:'auto', marginTop:6 }}>
                    {(unreadPrivateCount + systemUnreadCount) > 0 && (
                      <div style={{ padding:'8px 0', borderBottom:'1px solid rgba(255,255,255,0.08)', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                        <div>
                          <div style={{ fontWeight:600 }}>Neue Privater-Chat-Nachrichten</div>
                          <div style={{ color:'#cbd5e1', fontSize:13 }}>{unreadPrivateCount + systemUnreadCount} ungelesen</div>
                        </div>
                        <button onClick={()=>{ setNotifOpen(false); nav('/chat'); }}>Öffnen</button>
                      </div>
                    )}
                    {(notifs.length === 0 && (unreadPrivateCount + systemUnreadCount) === 0) ? (
                      <div style={{ color:'#9ca3af', fontSize:13 }}>Keine neuen Benachrichtigungen.</div>
                    ) : (
                      notifs.map(n => (
                        <div key={n.id} style={{ padding:'8px 0', borderBottom:'1px solid rgba(255,255,255,0.08)' }}>
                          <div style={{ fontWeight:600 }}>{n.title}</div>
                          {n.body && <div style={{ color:'#cbd5e1', fontSize:13 }}>{n.body}</div>}
                          <div style={{ marginTop:6 }}>
                            <button onClick={()=>markRead(n)}>{n.data?.link ? 'Öffnen' : 'Gelesen'}</button>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              )}
              {accountOpen && (
                <div className="apn-account-menu" onMouseLeave={() => setAccountOpen(false)}>
                  <Link to="/dashboard" onClick={() => setAccountOpen(false)}>Mein Dashboard</Link>
                  <Link to={`/u/${user.id}`} onClick={() => setAccountOpen(false)}>Profil anzeigen</Link>
                  <Link to="/profile" onClick={() => setAccountOpen(false)}>Profil bearbeiten</Link>
                  <Link to="/integrations" onClick={() => setAccountOpen(false)}>Kanal-Verknüpfungen</Link>
                  <Link to="/account/password" onClick={() => setAccountOpen(false)}>Passwort ändern</Link>
                  <Link to="/chat" onClick={() => setAccountOpen(false)}>
                    Privater-Chat
                  </Link>
                  {user.role === "admin" && (
                    <Link to="/admin/users" onClick={() => setAccountOpen(false)}>Benutzerverwaltung</Link>
                  )}
                  <button className="apn-account-logout" onClick={handleLogout}>Logout</button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <NewsTicker />
    </header>
  );
}

function NewsTicker() {
  const [items, setItems] = useState([]);
  const fetchingRef = useRef({ fetching: false, lastAt: 0 });

  async function fetchTicker() {
    const now = Date.now();
    if (fetchingRef.current.fetching) return;
    if (now - (fetchingRef.current.lastAt || 0) < 2000) return;
    fetchingRef.current.fetching = true;
    fetchingRef.current.lastAt = now;
    try {
      const endpoints = ['/social-feed/ticker', '/api/social-feed/ticker'];
      let data = null;
      for (const url of endpoints) {
        try {
          const res = await api.get(url, { params: { limit: 25 } });
          data = res?.data || null;
          break;
        } catch (e) {
          const st = e?.response?.status;
          // If one path 404s, try the other.
          if (st === 404) continue;
          throw e;
        }
      }
      const next = Array.isArray(data?.items) ? data.items : [];
      setItems(next);
    } catch (e) {
    } finally {
      fetchingRef.current.fetching = false;
    }
  }

  useEffect(() => {
    fetchTicker();
    // periodic safety refresh (in case animation events are throttled)
    const t = setInterval(fetchTicker, 10 * 60 * 1000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!Array.isArray(items) || items.length === 0) return null;

  const content = (
    <span className="apn-news-ticker-segment">
      {items.map((it, idx) => {
        const sep = idx === 0 ? null : <span className="apn-news-ticker-sep">•</span>;
        const text = String(it.text || '').trim();
        if (!text) return null;
        const link = it.url ? (
          <a key={it.id} href={it.url} target="_blank" rel="noreferrer">
            {text}
          </a>
        ) : (
          <span key={it.id}>{text}</span>
        );
        return (
          <span key={`it-${it.id}-${idx}`} className="apn-news-ticker-item">
            {sep}
            {link}
          </span>
        );
      })}
    </span>
  );

  return (
    <div className="apn-news-ticker" aria-label="News-Ticker">
      <div
        className="apn-news-ticker-track"
        onAnimationIteration={() => {
          // refresh after a full loop (25 items passed)
          fetchTicker();
        }}
      >
        {content}
        {content}
      </div>
    </div>
  );
}
